// streamUpload — the browser side of Cloudflare Stream.
//
// Extracted from CuratedVideosScreen when member uploads (043) gave these three
// helpers a second caller. Both the admin curated-video screen and
// SubmitVideoScreen use this module; do not re-implement any of it locally.
//
// The bytes go browser → Cloudflare directly. No video byte transits Supabase or
// Vercel, which is what makes a real progress bar possible and keeps the video
// off our own bandwidth bill.

import { supabase } from './supabase';

// Authorization is attached by hand: supabase.functions.invoke does not send it,
// and cf-stream runs with Verify JWT disabled so its own preamble is the only
// auth. Same workaround VerifyModal.jsx uses.
export const callCfStream = async (body) => {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session) throw new Error('Your session expired — sign in again.');

  const res = await fetch(`${process.env.REACT_APP_SUPABASE_URL}/functions/v1/cf-stream`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      apikey: process.env.REACT_APP_SUPABASE_ANON_KEY,
      Authorization: `Bearer ${session.access_token}`,
    },
    body: JSON.stringify(body),
  });

  const json = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(json.error || `Request failed (${res.status})`);
  return json;
};

// XHR rather than fetch purely for upload progress — fetch still cannot report
// it.
//
// Returns an object with an `abort()` alongside the promise: a member on
// cellular needs a way out of a stalled 200 MB upload, which the admin screen
// never needed.
export const uploadToCloudflare = (uploadURL, file, onProgress) => {
  const xhr = new XMLHttpRequest();
  const promise = new Promise((resolve, reject) => {
    xhr.open('POST', uploadURL);
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable && onProgress) onProgress(Math.round((e.loaded / e.total) * 100));
    };
    xhr.onload = () => (xhr.status >= 200 && xhr.status < 300
      ? resolve()
      : reject(new Error(`Upload failed (${xhr.status}). The upload link may have expired — try again.`)));
    xhr.onerror = () => reject(new Error('Upload failed — network error.'));
    xhr.onabort = () => reject(new Error('aborted'));
    const fd = new FormData();
    fd.append('file', file);
    xhr.send(fd);
  });
  promise.abort = () => xhr.abort();
  return promise;
};

// Cloudflare transcodes after upload, so a video is not playable the moment the
// bytes land.
//
// ⚠️ Returning null on timeout is not a failure and must stay that way: the row
// exists, cf-stream's `status` action is idempotent, and the next visit (the
// member's own list, or a reviewer opening the queue) reconciles it. Treating a
// slow transcode as an error is what would strand a row — and, for a member
// upload, a Cloudflare asset that is being billed for.
export const pollUntilReady = async (uid, {
  timeoutMs = 10 * 60_000,
  intervalMs = 3000,
  submissionId,
  onProgress,
} = {}) => {
  const deadline = Date.now() + timeoutMs;
  while (Date.now() < deadline) {
    const s = await callCfStream({
      action: 'status',
      uid,
      // A member may only poll their own submission, so cf-stream needs to know
      // which row this uid is supposed to belong to.
      ...(submissionId ? { submission_id: submissionId } : {}),
    });
    if (onProgress && typeof s.pctComplete === 'number') onProgress(s.pctComplete);
    if (s.ready) return s;
    if (s.state === 'error') {
      throw new Error(s.errorReasonText || 'Cloudflare could not process this video.');
    }
    await new Promise((r) => setTimeout(r, intervalMs));
  }
  return null;
};

// Cloudflare's basic direct-creator upload accepts at most 200 MB per file and
// there is no maxSizeBytes parameter to set — that ceiling IS the size cap.
// Raising it needs tus resumable upload. Kept here so the client can say so
// before a member spends ten minutes uploading something that will be refused.
export const MEMBER_MAX_BYTES = 200 * 1024 * 1024;
export const MEMBER_MAX_DURATION_SECONDS = 600;

export const formatBytes = (bytes) => {
  if (!bytes) return '';
  const mb = bytes / (1024 * 1024);
  return mb >= 1024 ? `${(mb / 1024).toFixed(1)} GB` : `${Math.round(mb)} MB`;
};

// Duration is read from the file in the browser only to fail fast with a clear
// message. Cloudflare enforces the real limit at ingest (maxDurationSeconds),
// because anything measured in the browser can be lied about.
export const readVideoDuration = (file) =>
  new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const el = document.createElement('video');
    el.preload = 'metadata';
    el.onloadedmetadata = () => {
      URL.revokeObjectURL(url);
      resolve(Number.isFinite(el.duration) ? el.duration : null);
    };
    el.onerror = () => { URL.revokeObjectURL(url); resolve(null); };
    el.src = url;
  });
